import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { Children, useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/utils';

export type CarouselProps = {
  children: React.ReactNode;
  /**
   * Auto-advance interval in ms. Omit to disable autoplay. Autoplay pauses on
   * hover/focus/drag and is disabled entirely when the user prefers reduced
   * motion.
   */
  autoplayInterval?: number;
  /**
   * Called with the active slide index on mount and whenever it changes —
   * fires for a single slide too, so impression tracking needs no special
   * casing upstream.
   */
  onSlideChange?: (index: number) => void;
  loop?: boolean;
  className?: string;
  'aria-label': string;
};

const prefersReducedMotion = (): boolean =>
  typeof globalThis.matchMedia === 'function' &&
  globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function Carousel({
  children,
  autoplayInterval,
  onSlideChange,
  loop = true,
  className,
  'aria-label': ariaLabel,
}: Readonly<CarouselProps>) {
  const slides = Children.toArray(children);
  const isCarousel = slides.length > 1;

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop, align: 'start', active: isCarousel },
    autoplayInterval && isCarousel && !prefersReducedMotion()
      ? [
          Autoplay({
            delay: autoplayInterval,
            stopOnInteraction: false,
            stopOnMouseEnter: true,
            stopOnFocusIn: true,
          }),
        ]
      : []
  );

  const [selectedIndex, setSelectedIndex] = useState(0);

  // Keep the latest callback in a ref so embla listeners never go stale
  // without needing re-subscription on every render.
  const onSlideChangeRef = useRef(onSlideChange);
  onSlideChangeRef.current = onSlideChange;

  // The effect re-runs once the embla API resolves; guard so the initial
  // slide is only reported once.
  const reportedInitialSlide = useRef(false);

  useEffect(() => {
    if (!reportedInitialSlide.current) {
      reportedInitialSlide.current = true;
      onSlideChangeRef.current?.(0);
    }
    if (!emblaApi) return () => {};

    const onSelect = () => {
      const index = emblaApi.selectedScrollSnap();
      setSelectedIndex(index);
      onSlideChangeRef.current?.(index);
    };
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index);
      // Restart the autoplay interval so a manual selection gets the full
      // interval before auto-advancing (no-op while autoplay is paused).
      emblaApi?.plugins().autoplay?.reset();
    },
    [emblaApi]
  );

  if (!isCarousel) {
    return <div className={className}>{slides[0] ?? null}</div>;
  }

  return (
    <section
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      // min-w-0 keeps the slides' combined min-content width from inflating
      // ancestor flex items (min-width:auto would let it propagate).
      className={cn('relative min-w-0 max-w-full', className)}
    >
      <div ref={emblaRef} className="overflow-hidden">
        {/* Slides have no stable id at this level; order is the identity. */}
        <div className="flex">
          {slides.map((slide, index) => (
            <div
              key={index}
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${slides.length}`}
              className="min-w-0 flex-[0_0_100%]"
            >
              {slide}
            </div>
          ))}
        </div>
      </div>
      {/* Overlaid on md+ so the carousel adds no height beyond its slides;
          below the slides on mobile where card bottoms carry content */}
      <div className="z-10 flex justify-center gap-2 max-md:mt-3 md:absolute md:bottom-3 md:left-1/2 md:-translate-x-1/2">
        {slides.map((_, index) => (
          <button
            key={index}
            type="button"
            aria-label={`Go to slide ${index + 1}`}
            aria-current={index === selectedIndex}
            onClick={() => scrollTo(index)}
            className={cn(
              'h-2 rounded-full transition-all duration-300',
              index === selectedIndex
                ? 'w-6 bg-bhanzu-orange'
                : 'w-2 bg-magical-moonlight-d1/40 hover:bg-magical-moonlight-d1'
            )}
          />
        ))}
      </div>
    </section>
  );
}
